import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import {
  CLEANING_TYPE_IDS,
  CLEANING_TYPE_LABELS,
  defaultPricingConfig,
  formatCents,
  getBookingPrice,
  includesFreeExtras,
} from '@homero/pricing';
import { useBasePrices } from '../../hooks/useBasePrices';
import { Button } from '../styles/Hero.styles';

const bedrooms = [...new Array(6)].map((_, index) =>
  index === 0
    ? { label: 'Studio', value: index }
    : { label: `${index} Bedroom${index > 1 ? 's' : ''}`, value: index }
);

const bathrooms = [...new Array(5)].map((_, index: number) => ({
  label: `${index + 1} Bathroom${index + 1 > 1 ? 's' : ''}`,
  value: index + 1,
}));

/**
 * Built from the shared rate card rather than written out here.
 *
 * This list was a hardcoded copy of the **V2** prices (108/158/248/228) long
 * after the booking app moved to V4 (128/183/288/273). Two consequences: the
 * advertised "Book from $X" was wrong by $20-$60, and the `?t=<price>` handoff
 * carried a price the booking app's exact-match check rejected, so every
 * cleaning type a customer picked here was silently discarded and they landed
 * on Standard.
 */
const types = CLEANING_TYPE_IDS.map((id) => ({
  label: CLEANING_TYPE_LABELS[id],
  /** The stable id, so the handoff survives a price change. */
  value: id,
}));

export default function Booking({ center = false, openBooking }) {
  const [bedroom, setBedroom] = useState(bedrooms[0]);
  const [bathroom, setBathroom] = useState(bathrooms[0]);
  const [type, setType] = useState(types[0]);

  /**
   * The prices actually in force, with the bundled card as the fallback.
   *
   * Without this the teaser is computed entirely from the rate card compiled
   * into this bundle, so every change an admin makes in the pricing editor
   * widens the gap between "Book from $X" here and the total quoted one click
   * later in the booking funnel. Only the rate card is overridden — the
   * cleaning-type prices and the room table — because a teaser has no date, so
   * there is no day-of-week surge to apply.
   */
  const basePrices = useBasePrices();
  const config = useMemo(() => {
    const bundled = defaultPricingConfig(new Date());
    if (!basePrices) return bundled;
    return {
      ...bundled,
      rateCard: {
        ...bundled.rateCard,
        cleaningTypes: {
          ...bundled.rateCard.cleaningTypes,
          ...basePrices.cleaningTypes,
        },
        roomCents: basePrices.roomCents ?? bundled.rateCard.roomCents,
      },
    };
  }, [basePrices]);

  /**
   * The teaser price, computed with the same defaults the booking app starts
   * with — flexible arrival, and the every-2-weeks discount unless this
   * cleaning type already includes the extras. It is therefore the price the
   * customer actually sees on the next screen, which the hardcoded 20%/-$10
   * approximation was not.
   */
  const getPrice = () => {
    const createdAt = new Date();
    const includesExtras = includesFreeExtras(type.value, createdAt);
    const { totalCents } = getBookingPrice(
      {
        bedrooms: bedroom.value,
        bathrooms: bathroom.value,
        cleaningType: type.value,
        clean_time: 'flexible',
        frequencyDiscountPercent: includesExtras ? 0 : 15,
        add_ons: [],
        created_at: createdAt,
      },
      0,
      config
    );
    return formatCents(totalCents);
  };

  return (
    <div
      className={`d-flex book-select ${
        center ? 'justify-content-center' : 'justify-content-start'
      }`}
      style={{ zIndex: 10 }}
    >
      <Select
        className="select select-radius"
        defaultValue={bedroom}
        // @ts-ignore
        onChange={(e) => setBedroom(e)}
        options={bedrooms}
        isSearchable={false}
        menuPortalTarget={document.getElementById('#root')}
      />
      <Select
        className="select"
        defaultValue={bathroom}
        // @ts-ignore
        onChange={(e) => setBathroom(e)}
        options={bathrooms}
        isSearchable={false}
      />
      <Select
        className="select"
        defaultValue={type}
        // @ts-ignore
        onChange={(e) => setType(e)}
        options={types}
        isSearchable={false}
      />
      <Button
        onClick={() =>
          openBooking({
            bedrooms: bedroom.value,
            bathrooms: bathroom.value,
            type: type.value,
          })
        }
        // @ts-ignore
        hoverBackground="#0093ED"
        className="booking-button"
      >
        Book from {getPrice()}
      </Button>
    </div>
  );
}
